import React, { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Building2, LayoutList, UserRound, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import { useAuthStore } from '@/store/auth';

/**
 * Rotas do editor trazem a propria barra de ferramentas (FormBuilder.tsx),
 * com titulo editavel e controles de preview/publicar. Renderizar o Header
 * global acima dela produziria duas barras empilhadas, entao nestas rotas
 * o editor e a unica barra e a sidebar desaparece.
 */
const isBuilderRoute = (pathname: string) =>
  pathname === '/forms/new' || /^\/forms\/[^/]+\/edit\/?$/.test(pathname);

const AppLayout: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { pathname } = useLocation();
  const { t } = useTranslation('common');
  const tt = (key: string, fallback: string) =>
    t(key, { defaultValue: fallback });
  const role = useAuthStore((s) => s.user?.role);
  const tenantId = useAuthStore((s) => s.user?.tenantId);
  const isAdmin = role === 'owner' || role === 'admin';
  const isPlatform = isAdmin && tenantId === 'staff';
  const builder = isBuilderRoute(pathname);

  const items = [
    { to: '/forms', label: tt('nav.forms', 'Formulários'), Icon: LayoutList },
    ...(isAdmin
      ? [
          {
            to: '/admin/users',
            label: tt('nav.users', 'Usuários'),
            Icon: Users,
          },
        ]
      : []),
    ...(isPlatform
      ? [
          {
            to: '/admin/tenants',
            label: tt('nav.tenants', 'Tenants'),
            Icon: Building2,
          },
        ]
      : []),
    { to: '/profile', label: tt('nav.profile', 'Perfil'), Icon: UserRound },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 dark:bg-slate-950">
      {!builder && (
        <Header onToggleSidebar={() => setSidebarCollapsed((v) => !v)} />
      )}
      <div className="flex min-h-0 flex-1">
        {!builder && (
          <aside
            data-collapsed={sidebarCollapsed}
            className={`sticky top-14 flex h-[calc(100vh-3.5rem)] shrink-0 flex-col border-r border-slate-200 bg-white py-4 transition-[width] dark:border-slate-800 dark:bg-slate-900 ${
              sidebarCollapsed ? 'w-16' : 'w-56'
            }`}
          >
            <nav className="flex flex-1 flex-col gap-1 px-2">
              {items.map(({ to, label, Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  title={label}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                    } ${sidebarCollapsed ? 'justify-center' : ''}`
                  }
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  {!sidebarCollapsed && (
                    <span className="truncate">{label}</span>
                  )}
                </NavLink>
              ))}
            </nav>
          </aside>
        )}
        <main
          className="min-w-0 flex-1 overflow-x-hidden"
          data-sidebar-collapsed={sidebarCollapsed}
        >
          {builder ? (
            <Outlet />
          ) : (
            <div className="mx-auto w-full max-w-[1920px] px-3 py-6 sm:px-4 lg:px-5">
              <Outlet />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
